import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTableDataSearchDto,
  TableCacheKeyDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useMutation } from '@tanstack/react-query';
import { type ICellRendererParams } from 'ag-grid-community';

import { updateTableRows } from '../../api/portalGateway/updateTableRows';
import { actionWithAsyncNotification } from '../../utils/actionWithAsyncNotification';
import { extractErrorMessage } from '../../utils/errorMessage';
import { invalidateTable } from '../../utils/queryCacheHelper';

import { getRowId } from './widgetBulkAction';

/**
 * Values that can be saved from an inline cell edit.
 * Checkbox booleans only in this iteration. Widen this when more column types are supported.
 */
export type InlineCellValue = boolean;

function pendingKey(rowId: string, columnKey: string) {
  return `${rowId}:${columnKey}`;
}

type RowNode = ICellRendererParams<TableRowDto['data']>['node'];

/** Search loaded grid nodes for the row with the given id. */
function findLoadedNode(
  api: ICellRendererParams<TableRowDto['data']>['api'],
  rowId: string
): RowNode | undefined {
  let found: RowNode | undefined;
  api.forEachNode((node) => {
    if (found == null && node.data != null && getRowId(node.data) === rowId) {
      found = node;
    }
  });
  return found;
}

type UseInlineCellEditingParams = {
  contentId: string;
  cacheKey: TableCacheKeyDto;
  pageId: string;
  pageFilterSelections: DataTableDataSearchDto['selection'];
};

/**
 * Optimistic single-field save for inline-editable cells.
 * The caller decides which columns are editable and which value to send.
 * Checkbox is the only column type wired up in this iteration.
 */
export function useInlineCellEditing({
  contentId,
  cacheKey,
  pageId,
  pageFilterSelections,
}: UseInlineCellEditingParams) {
  const { t } = useTranslation();
  const pendingKeysRef = useRef(new Set<string>());
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const { mutateAsync } = useMutation({ mutationFn: updateTableRows });

  const publishPendingKeys = useCallback((next: Set<string>) => {
    pendingKeysRef.current = next;
    setPendingKeys(next);
  }, []);

  const isInlineCellPending = useCallback(
    (rowId: string, columnKey: string) =>
      pendingKeys.has(pendingKey(rowId, columnKey)),
    [pendingKeys]
  );

  const saveInlineCell = useCallback(
    (
      params: ICellRendererParams<TableRowDto['data']>,
      columnKey: string,
      nextValue: InlineCellValue
    ) => {
      const rowData = params.node.data;
      if (rowData == null || params.node.group) {
        return;
      }

      const rowId = getRowId(rowData);
      if (rowId == null) {
        return;
      }

      const key = pendingKey(rowId, columnKey);
      if (pendingKeysRef.current.has(key)) {
        return;
      }

      const nextPending = new Set(pendingKeysRef.current);
      nextPending.add(key);
      publishPendingKeys(nextPending);

      const previousValue: unknown = params.value;
      params.node.setDataValue(columnKey, nextValue);

      void actionWithAsyncNotification(
        async () => {
          try {
            await mutateAsync({
              contentId,
              rowIds: [rowId],
              data: {
                data: { [columnKey]: nextValue },
                context: { pageFilterSelections },
              },
            });
          } catch (error) {
            const target =
              params.node.data != null && getRowId(params.node.data) === rowId
                ? params.node
                : findLoadedNode(params.api, rowId);
            target?.setDataValue(columnKey, previousValue ?? null);
            throw error;
          }
        },
        {
          startMessage: t('app.notifications.inlineEdit.loading'),
          successMessage: t('app.notifications.inlineEdit.success'),
          errorMessage: (error) =>
            extractErrorMessage(error, t('app.notifications.inlineEdit.error')),
          onSuccess: async () => {
            await invalidateTable(cacheKey, pageId, contentId);
          },
          onFinished: () => {
            const withoutKey = new Set(pendingKeysRef.current);
            withoutKey.delete(key);
            publishPendingKeys(withoutKey);
          },
        }
      );
    },
    [
      cacheKey,
      contentId,
      mutateAsync,
      pageFilterSelections,
      pageId,
      publishPendingKeys,
      t,
    ]
  );

  return {
    saveInlineCell,
    isInlineCellPending,
  };
}
